import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Info, X } from "@digital-twins/design-system/icons";

import { cn } from "@/lib/utils";
import { LanguageChip } from "./LanguageChip";
import { chipStrings } from "./strings";
import {
  STUDY_LANGUAGE_COACHMARK_AUTO_DISMISS_MS,
  dismissStudyLanguageCoachmark,
  hasDismissedStudyLanguageCoachmark,
} from "./studyLanguageCoachmarkStorage";

export const STUDY_LANGUAGE_COACHMARK_FADE_MS = 300;

interface StudyLanguageCoachmarkProps {
  language: string;
  onLanguageChange: (lang: string) => void;
  /** Locks the chip (the builder, while a turn is in flight). */
  disabled?: boolean;
  popoverAlign?: "start" | "center" | "end";
  className?: string;
}

type CoachmarkPhase = "open" | "closing" | "closed";

export function StudyLanguageCoachmark(props: StudyLanguageCoachmarkProps) {
  const { language, onLanguageChange, disabled, popoverAlign, className } =
    props;
  const anchorRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<CoachmarkPhase>(() =>
    hasDismissedStudyLanguageCoachmark() ? "closed" : "open",
  );
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const requestClose = useCallback(() => {
    setPhase((current) => (current === "open" ? "closing" : current));
  }, []);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    setPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
  }, []);

  useLayoutEffect(() => {
    if (phase === "closed") {
      return;
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [phase, updatePosition]);

  useEffect(() => {
    if (phase !== "open") {
      return;
    }

    const timer = window.setTimeout(() => {
      requestClose();
    }, STUDY_LANGUAGE_COACHMARK_AUTO_DISMISS_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [phase, requestClose]);

  useEffect(() => {
    if (phase !== "closing") {
      return;
    }

    const timer = window.setTimeout(() => {
      dismissStudyLanguageCoachmark();
      setPhase("closed");
    }, STUDY_LANGUAGE_COACHMARK_FADE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [phase]);

  const coachmark =
    phase === "closed" ? null : (
      <div
        role="status"
        aria-live="polite"
        data-testid="study-language-coachmark"
        data-phase={phase}
        style={{ top: position.top, left: position.left }}
        className={cn(
          "pointer-events-auto fixed z-[100] w-[min(calc(100vw-2rem),16.5rem)] -translate-y-1/2 transition-opacity duration-300 ease-out",
          phase === "open"
            ? "animate-in fade-in duration-300 opacity-100"
            : "opacity-0",
        )}
      >
        <div
          className={cn(
            "relative rounded-xl border px-3.5 py-3 pr-9",
            "border-border/70 bg-white text-foreground shadow-[0_10px_30px_rgba(15,23,42,0.08)]",
            "dark:border-border dark:bg-card dark:shadow-[0_12px_32px_rgba(0,0,0,0.28)]",
          )}
        >
          <div
            aria-hidden="true"
            className={cn(
              "absolute -left-[7px] top-1/2 h-3.5 w-3.5 -translate-y-1/2 rotate-45 border-b border-l",
              "border-border/70 bg-white dark:border-border dark:bg-card",
            )}
          />
          <div className="flex items-start gap-2.5">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </div>
            <p className="text-[13px] leading-[1.45] text-muted-foreground">
              {chipStrings.languageCoachmarkBody}
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            aria-label={chipStrings.languageCoachmarkDismissAriaLabel}
            data-testid="study-language-coachmark-dismiss"
            className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );

  return (
    <>
      <div ref={anchorRef} className={cn("relative w-fit", className)}>
        <LanguageChip
          language={language}
          onLanguageChange={onLanguageChange}
          disabled={disabled}
          popoverAlign={popoverAlign}
          popoverSide="right"
          popoverCollisionPadding={16}
        />
      </div>
      {coachmark ? createPortal(coachmark, document.body) : null}
    </>
  );
}
